import * as React from 'react';
import axios, { AxiosError } from 'axios';

import useAsyncInterval from './useAsyncInterval';
import { getText } from '../../helpers';
import { ErrorManager } from '../../utilities/error-manager';

const GENERIC_ERROR_MESSAGE = getText('pages.solution.demos.kenshoScribe.genericError');
const PAYLOAD_TOO_LARGE_MESSAGE = getText('pages.solution.demos.kenshoScribe.payloadTooLarge');
const RATE_LIMIT_EXCEEDED_MESSAGE = getText('pages.solution.demos.kenshoScribe.limitHit');
const UNAUTHORIZED_MESSAGE = getText('pages.solution.demos.kenshoScribe.authError');
const REQUEST_TIMED_OUT = getText('pages.solution.demos.kenshoScribe.timeout');
const KENSHO_SCRIBE_URL = 'https://scribe.kensho.com/api/v1/transcription';

const EXPECTED_RESPONSE_CODES = new Set([403, 413, 429]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isUnexpectedResponse(errorResponse: AxiosError<any>): boolean {
    return errorResponse.response == null || (errorResponse.response && !EXPECTED_RESPONSE_CODES.has(errorResponse.response.status));
}

function getErrorMessage(response: {status?: number} | Error): string {
    if (response instanceof Error) {
        switch (response.message) {
            case 'Request timed out':
                return REQUEST_TIMED_OUT;
            default:
                return response.message;
        }
    }
    switch (response.status) {
        case 403:
            return UNAUTHORIZED_MESSAGE;
        case 413:
            return PAYLOAD_TOO_LARGE_MESSAGE;
        case 429:
            return RATE_LIMIT_EXCEEDED_MESSAGE;
        default:
            return GENERIC_ERROR_MESSAGE;
    }
}

function reducer(state: TranscriptionState, action: TranscriptionAction): TranscriptionState {
    switch (action.type) {
        case 'reset':
            return { status: 'uninitiated' };
        case 'error':
            return { ...state, error: getErrorMessage(action.error), status: 'error' };
        case 'transcriptIdReceived':
            if (state.status === 'uninitiated') return state;
            return { ...state, transcriptId: action.uploadResponse.unique_id, status: 'loading' };
        case 'transcriptReceived':
            if (state.status === 'uninitiated') return state;
            if (action.transcriptResponse.error) {
                return { ...state, error: action.transcriptResponse.error, status: 'error' };
            }
            if (action.transcriptResponse.status === 'pending') {
                return state;
            }
            return { ...state, transcript: action.transcriptResponse.data, status: 'success' };
        case 'requestInitiated':
            return { ...state, status: 'initiated' };
        default:
            throw new Error('Invalid action');
    }
}

export default function useTranscriptionRequest(token: string | null, onUnauthorizedResponse: () => void): {
  error?: string;
  transcribeSpeechFile: (file: File) => void;
  transcript?: APITranscript;
  status: TranscriptionStatus;
  resetRequest: () => void;
} {

    const [state, dispatch] = React.useReducer(reducer, {
        status: 'uninitiated',
    });

    const transcribeSpeechFile = React.useCallback(
        (speechFile: File) => {
            if (speechFile && token != null) {
                // If the transcription job id exists, then it's still valid since the audio file hasn't changed
                if (state.transcriptId === undefined) {
                    dispatch({ type: 'requestInitiated' });
                    axios.post<APIUploadResponse>(
                        KENSHO_SCRIBE_URL,
                        speechFile,
                        {
                            headers: {
                                'Content-Type': 'application/mp3',
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    )
                        .then(response => dispatch({ type: 'transcriptIdReceived', uploadResponse: response.data }))
                        .catch(errorResponse => {
                            if (errorResponse.response && errorResponse.response.status === 403) onUnauthorizedResponse();
                            // Only log unexpected errors
                            if (isUnexpectedResponse(errorResponse)) ErrorManager.apiError(errorResponse, { msg: 'Transcription request error.' });
                            dispatch({ type: 'error', error: errorResponse });
                        });
                }
            }
        },
        [state.transcriptId, KENSHO_SCRIBE_URL, token]
    );

    const pollTranscriptionJob = React.useCallback(() => {
        const fetchOptions = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
        return axios.get(
            `${KENSHO_SCRIBE_URL}?unique_id=${state.transcriptId}`,
            fetchOptions
        );
    }, [state.transcriptId, KENSHO_SCRIBE_URL, token]);

    const onResolve = React.useCallback(
        response => dispatch({ type: 'transcriptReceived', transcriptResponse: response.data }),
        []
    );

    const onReject = React.useCallback(error => dispatch({ type: 'error', error }), []);

    useAsyncInterval(
        pollTranscriptionJob,
        state.status === 'loading' && state.transcriptId !== null ? 1000 : null,
        onResolve,
        onReject
    );

    const resetRequest = (): void => dispatch({ type: 'reset' });

    return { ...state, transcribeSpeechFile, resetRequest };
}
